import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ExternalLink, Pencil, Trash2 } from 'lucide-react'
import { apiAssetUrl } from '../../config'
import { adminApi } from '../api'
import { Btn, Card, DetailSkeleton } from '../ui'
import { formatDate, StatusBadge, type InsightDetail } from './insightsShared'

type FaqItem = { q: string; a: string }

function parseFaq(raw: unknown): FaqItem[] {
  if (!raw) return []
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((item) => ({
        q: String(item?.q || item?.question || ''),
        a: String(item?.a || item?.answer || ''),
      }))
      .filter((item) => item.q && item.a)
  } catch {
    return []
  }
}

export default function InsightViewPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [insight, setInsight] = useState<InsightDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      const { res, data } = await adminApi<{ insight?: InsightDetail; message?: string }>(
        `/api/admin/website/insights/${id}`
      )
      if (cancelled) return
      if (!res.ok || !data.insight) {
        setError(data.message || 'Insight not found')
        setInsight(null)
      } else {
        setInsight(data.insight)
      }
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [id])

  const faqs = useMemo(() => parseFaq(insight?.faq_json), [insight?.faq_json])
  const takeaways = useMemo(
    () =>
      String(insight?.key_takeaways || '')
        .split(/\n+/)
        .map((s) => s.trim())
        .filter(Boolean),
    [insight?.key_takeaways]
  )
  const howTo = useMemo(() => {
    if (!insight?.how_to_json) return [] as { name: string; text: string }[]
    try {
      const parsed = typeof insight.how_to_json === 'string' ? JSON.parse(insight.how_to_json) : insight.how_to_json
      if (!Array.isArray(parsed)) return []
      return parsed
        .map((item: { name?: string; text?: string }, i: number) => ({
          name: String(item?.name || `Step ${i + 1}`),
          text: String(item?.text || ''),
        }))
        .filter((s: { name: string; text: string }) => s.name && s.text)
    } catch {
      return []
    }
  }, [insight?.how_to_json])
  const relatedQueries = useMemo(
    () =>
      String(insight?.related_queries || '')
        .split(/\n+/)
        .map((s) => s.trim())
        .filter(Boolean),
    [insight?.related_queries]
  )

  async function remove() {
    if (!insight || !confirm('Delete this insight?')) return
    await adminApi(`/api/admin/website/insights/${insight.id}`, { method: 'DELETE' })
    navigate('/admin/insights', { replace: true })
  }

  if (loading) {
    return <DetailSkeleton />
  }

  if (error || !insight) {
    return (
      <Card className="space-y-4">
        <p className="text-sm text-red-600">{error || 'Not found'}</p>
        <Btn variant="ghost" onClick={() => navigate('/admin/insights')}>
          <ArrowLeft className="h-4 w-4" /> Back to list
        </Btn>
      </Card>
    )
  }

  const seoTitle = insight.seo_title || insight.title
  const seoDesc = insight.seo_description || insight.summary || ''

  return (
    <div className="space-y-4 sm:space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Btn variant="ghost" onClick={() => navigate('/admin/insights')}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Btn>
        <div className="ml-auto flex flex-wrap gap-2">
          <a
            href={`/insights/${insight.slug}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-[40px] items-center gap-1.5 rounded-2xl border border-primary/15 bg-white px-3.5 py-2 text-sm font-semibold text-primary"
          >
            Open on site <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <Link to={`/admin/insights/${insight.id}/edit`}>
            <Btn variant="ghost">
              <Pencil className="h-4 w-4" /> Edit
            </Btn>
          </Link>
          <Btn variant="danger" onClick={() => void remove()}>
            <Trash2 className="h-4 w-4" /> Delete
          </Btn>
        </div>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_300px] gap-4 sm:gap-5 items-start">
        <Card className="space-y-4 !p-5 sm:!p-7">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge published={!!insight.is_published} />
            {insight.noindex ? (
              <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[0.7rem] font-bold uppercase text-amber-800">
                Noindex
              </span>
            ) : null}
            <span className="text-xs text-slate-500">{formatDate(insight.published_at)}</span>
            <span className="text-xs text-slate-400">/{insight.slug}</span>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-navy-900 leading-tight">
            {insight.title}
          </h2>
          {insight.summary && <p className="text-sm sm:text-base text-slate-600">{insight.summary}</p>}
          {insight.cover_image_path && (
            <img
              src={apiAssetUrl(insight.cover_image_path)}
              alt=""
              className="w-full max-h-80 object-cover rounded-2xl border border-primary/10"
            />
          )}

          {insight.ai_summary && (
            <div className="rounded-2xl border border-primary/15 bg-primary/[0.04] p-4">
              <p className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-primary mb-2">
                Direct answer (AEO)
              </p>
              <p className="text-sm text-navy-900 leading-relaxed">{insight.ai_summary}</p>
            </div>
          )}

          {takeaways.length > 0 && (
            <div className="rounded-2xl border border-accent/30 bg-accent/10 p-4">
              <p className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-navy-900 mb-2">
                Key takeaways
              </p>
              <ul className="space-y-1.5 text-sm text-navy-900">
                {takeaways.map((t) => (
                  <li key={t} className="flex gap-2">
                    <span className="text-primary font-bold">•</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {howTo.length > 0 && (
            <div className="rounded-2xl border border-primary/10 p-4 space-y-3">
              <p className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-navy-900">How-to steps</p>
              <ol className="space-y-2 text-sm text-navy-900 list-decimal pl-4">
                {howTo.map((s) => (
                  <li key={s.name}>
                    <span className="font-semibold">{s.name}</span>
                    <p className="text-slate-600 mt-0.5">{s.text}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div
            className="prose prose-sm sm:prose max-w-none prose-headings:font-display prose-a:text-primary prose-img:rounded-xl prose-table:w-full border-t border-primary/10 pt-5"
            dangerouslySetInnerHTML={{
              __html: insight.content_html || '<p class="text-slate-400">No content</p>',
            }}
          />

          {faqs.length > 0 && (
            <div className="border-t border-primary/10 pt-5 space-y-3">
              <p className="font-display text-lg font-bold text-navy-900">FAQ</p>
              {faqs.map((f, i) => (
                <div key={i} className="rounded-2xl border border-primary/10 bg-slate-50/70 p-4">
                  <p className="font-semibold text-navy-900">{f.q}</p>
                  <p className="mt-1.5 text-sm text-slate-600 whitespace-pre-wrap">{f.a}</p>
                </div>
              ))}
            </div>
          )}
        </Card>

        <aside className="space-y-4 lg:sticky lg:top-24">
          <Card className="!p-4 space-y-3">
            <p className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-slate-500">
              SEO · AEO · GEO
            </p>
            <MetaRow label="Focus keyword" value={insight.focus_keyword} />
            <MetaRow label="Secondary keywords" value={insight.secondary_keywords} />
            <MetaRow label="SEO title" value={seoTitle} />
            <MetaRow label="Meta description" value={seoDesc} />
            <MetaRow label="AI summary" value={insight.ai_summary} />
            <MetaRow label="Intent" value={insight.search_intent} />
            <MetaRow label="Schema" value={insight.schema_type} />
            <MetaRow
              label="Author"
              value={[insight.author_name, insight.author_role].filter(Boolean).join(' · ')}
            />
            <MetaRow label="Primary city" value={insight.primary_city} />
            <MetaRow label="Locations" value={insight.target_locations} />
            <MetaRow label="Service area" value={insight.service_area} />
            <MetaRow
              label="Canonical"
              value={insight.canonical_path || `/insights/${insight.slug}`}
            />
            <MetaRow
              label="Robots"
              value={[
                insight.noindex ? 'noindex' : 'index',
                insight.nofollow ? 'nofollow' : 'follow',
                insight.exclude_from_sitemap ? 'no-sitemap' : null,
              ]
                .filter(Boolean)
                .join(', ')}
            />
            {relatedQueries.length > 0 && (
              <div>
                <p className="text-[0.65rem] font-bold uppercase tracking-wide text-slate-400">
                  Related queries
                </p>
                <ul className="mt-1 space-y-0.5 text-sm text-navy-900">
                  {relatedQueries.map((q) => (
                    <li key={q}>· {q}</li>
                  ))}
                </ul>
              </div>
            )}
          </Card>

          <Card className="!p-4 space-y-2">
            <p className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-slate-500">SERP preview</p>
            <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-1">
              <p className="text-[#1a0dab] text-sm leading-snug font-medium line-clamp-2">{seoTitle}</p>
              <p className="text-[#006621] text-[0.7rem] truncate">
                digisutra.in{insight.canonical_path || `/insights/${insight.slug}`}
              </p>
              <p className="text-[#4d5156] text-xs leading-snug line-clamp-3">
                {seoDesc || 'No meta description'}
              </p>
            </div>
          </Card>
        </aside>
      </div>
    </div>
  )
}

function MetaRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-[0.65rem] font-bold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-sm text-navy-900 break-words">{value?.trim() || '—'}</p>
    </div>
  )
}
